import React, { useRef, useState } from "react";
import { ICompany } from "../../../customTypings/company/Company";
import { Formik, Form } from "formik";
import { useNavigate } from "react-router-dom";
import {
  EditFormActions,
  EditFormSaveCancelButtons,
  FormAlert,
} from "../../Forms";
import {
  companyValidationSchema,
  emptyStringToNull,
  hydratePostalAddressForForm,
  mapPostalAddressToApi,
  normalizePhoneInput,
  nullToEmptyString,
  showToastError,
  showToastSuccess,
} from "../../../utils";
import { useFormAutoFocus } from "../../../hooks";
import { FormikUnsavedChangesGuard, type LeaveSaveOutcome } from "@/features/unsavedChanges";
import { companyService } from "../../../services";
import { CompanyInfoFormGrid } from "./CompanyInfoFormGrid";

const emptyCompany: ICompany = {
  id: "",
  phone: "",
  contactEmail: "",
  addressLine1: "",
  addressLine2: "",
  addressLine3: "",
  town: "",
  county: "",
  country: "United Kingdom",
  postcode: "",
  website: "",
  invitationEmail: "",
};

function normalizeCompanyForForm(company?: ICompany): ICompany {
  if (!company) return { ...emptyCompany };
  return {
    ...emptyCompany,
    ...company,
    ...hydratePostalAddressForForm(company),
    phone: nullToEmptyString(company.phone),
    contactEmail: nullToEmptyString(company.contactEmail),
    website: nullToEmptyString(company.website),
    invitationEmail: nullToEmptyString(company.invitationEmail),
  };
}

function normalizeCompanyPayload(values: ICompany): ICompany {
  const address = mapPostalAddressToApi(values);
  return {
    ...values,
    phone: normalizePhoneInput(values.phone),
    contactEmail: nullToEmptyString(values.contactEmail).trim(),
    invitationEmail: nullToEmptyString(values.invitationEmail).trim(),
    website: emptyStringToNull(values.website),
    addressLine1: address.addressLine1,
    addressLine2: address.addressLine2,
    addressLine3: address.addressLine3,
    town: address.town,
    county: address.county,
    postcode: address.postcode,
    country: address.country,
  };
}

interface CompanyInfoFormProps {
  company?: ICompany;
  onSuccess?: () => void;
}

export const CompanyInfoForm: React.FC<CompanyInfoFormProps> = ({ company, onSuccess }) => {
  const navigate = useNavigate();
  const isEdit = Boolean(company?.id?.trim());
  const leaveAfterSaveRef = useRef(false);
  const saveOutcomeRef = useRef<LeaveSaveOutcome>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const initialValues = normalizeCompanyForForm(company);
  const formAutoFocusRef = useFormAutoFocus(true, [company?.id]);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={companyValidationSchema}
      onSubmit={async (values, { resetForm }) => {
        saveOutcomeRef.current = null;
        setSubmitError(null);
        try {
          const payload = normalizeCompanyPayload(values);
          const response = isEdit
            ? await companyService.update(payload)
            : await companyService.create(payload);

          if (response?.status === "Success") {
            saveOutcomeRef.current = "success";
            showToastSuccess(
              isEdit
                ? "Company information updated successfully"
                : "Company information created successfully"
            );
            resetForm({ values });
            onSuccess?.();
            if (leaveAfterSaveRef.current) return;
          } else {
            saveOutcomeRef.current = "failure";
            const errorMsg = response?.message || "An error occurred";
            setSubmitError(errorMsg);
            showToastError(errorMsg);
          }
        } catch (err: unknown) {
          saveOutcomeRef.current = "failure";
          const errorMsg = err instanceof Error ? err.message : "An error occurred";
          setSubmitError(errorMsg);
          showToastError(errorMsg);
        }
      }}
      enableReinitialize
    >
      {({ isSubmitting, handleSubmit }) => {
        const form = (
          <Form
            ref={formAutoFocusRef}
            noValidate
            onSubmit={handleSubmit}
          >
            <FormAlert message={submitError || undefined} variant="danger" />

            <CompanyInfoFormGrid />

            <EditFormActions>
              <EditFormSaveCancelButtons
                isSubmitting={isSubmitting}
                enableSaveBeforeLeave={isEdit}
                leaveAfterSaveRef={leaveAfterSaveRef}
                saveOutcomeRef={saveOutcomeRef}
                onCancel={() => navigate("/settings")}
              />
            </EditFormActions>
          </Form>
        );

        if (!isEdit) {
          return form;
        }

        return (
          <FormikUnsavedChangesGuard
            enableRouteBlocking
            isNewRecord={false}
            enableSaveBeforeLeave
            leaveAfterSaveRef={leaveAfterSaveRef}
            saveOutcomeRef={saveOutcomeRef}
          >
            {form}
          </FormikUnsavedChangesGuard>
        );
      }}
    </Formik>
  );
};
